import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import type { FieldFormProps } from '@helpcenter/field-types';
import type { WrappedComponentProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import { FieldBase } from 'view/common/fields/field-base';
import { Fieldset } from '@atlaskit/form/fieldset';
import { RequiredAsterisk } from '@atlaskit/form/required-asterisk';
import AkRadioGroup from '@atlaskit/radio/radio-group';
import { token } from '@atlaskit/tokens';
import i18n from './messages';
import { withControlledFormComponentValue } from 'view/common/with-controlled-form-component-value';
import { normalizeRadioButtonsValue } from '@atlassian/jsm-ui-modifications-view-request-create/src/common/utils/configuration/fields/radio-buttons';
import type { RadioValueOverride } from 'state/persisted/request-create/types';

interface Item {
	label: string;
	name: string;
	value: string;
}

interface Props extends FieldFormProps<string> {
	items?: Item[];
	defaultValue?: string;
	value?: string;
}

const NONE_OPTION_VALUE = '-1';

class RadioGroup extends React.PureComponent<Props & WrappedComponentProps> {
	onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
		if (!this.props.onChange) {
			return;
		}

		if (this.props.value !== undefined) {
			const selectedItem = this.props.items?.find((item) => item.value === e.target.value);
			this.props.onChange(this.props.id, e.target.value, false, {
				rawObject: selectedItem ?? {},
				rawValue: selectedItem ?? null,
			});
			return;
		}

		this.props.onChange(this.props.id, e.target.value);
	};

	render() {
		const {
			required,
			label,
			id,
			description,
			defaultValue,
			value,
			errorMessage,
			disabled,
			intl,
			items = [],
		} = this.props;

		const radioItems = items.map((item) => {
			return {
				label: item.label,
				name: item.name,
				value: item.value,
				isDisabled: disabled,
			};
		});

		if (!required) {
			radioItems.push({
				label: intl.formatMessage(i18n.noneOptionLabel),
				name: id,
				value: NONE_OPTION_VALUE,
				isDisabled: disabled,
			});
		}

		return (
			<FieldBase
				htmlFor={id}
				noLabel
				required={required}
				description={description}
				label={label}
				labelId={`label-${id}`}
				errorMessage={errorMessage}
			>
				<Fieldset
					legend={
						<>
							{label}
							{required && <RequiredAsterisk />}
						</>
					}
				>
					<RadioGroupWrapper>
						<AkRadioGroup
							isRequired={required}
							options={radioItems}
							onChange={this.onChange}
							{...(value === undefined ? { defaultValue } : { value })}
							aria-describedby={`${id}-field-description`}
						/>
					</RadioGroupWrapper>
				</Fieldset>
			</FieldBase>
		);
	}
}

const IntlRadioGroup = injectIntl(RadioGroup);

export const ControlledRadioGroup = withControlledFormComponentValue<RadioValueOverride | string>(
	(_field: string, value: string) => value,
	normalizeRadioButtonsValue,
)(IntlRadioGroup);

export default IntlRadioGroup;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const RadioGroupWrapper = styled.div`
	padding: ${token('space.075', '6px')};
`;
